<script setup lang="ts">
import { en, es } from '@nuxt/ui/locale'
import { tenantAppearanceCss } from '~/shared-kernel/domain/tenant-appearance'
import { useBrandingContextStore } from '~/shared-kernel/presentation/stores/branding-context.store'
import PwaInstallButton from '~/components/PwaInstallButton.vue'
import DataProcessingPolicyModal from '~/shared-kernel/presentation/legal/DataProcessingPolicyModal.vue'
import { useAbsoluteUrl } from '~/shared-kernel/presentation/composables/usePageSeo'

const { locale, t } = useI18n()
const runtimeConfig = useRuntimeConfig()
const branding = useBrandingContextStore()
await branding.resolve()
const brandName = computed(() => t('public.brand'))
const metaDescription = computed(() => t('app.meta.description'))
const uiLocale = computed(() => (locale.value === 'en' ? en : es))
const isImpeccableLiveEnabled = computed(() => {
  const enabled = runtimeConfig.public.impeccableLiveEnabled

  return import.meta.dev && ['1', 'true'].includes(String(enabled).toLowerCase())
})

const route = useRoute()
const switchLocalePath = useSwitchLocalePath()
const absoluteUrl = useAbsoluteUrl()
// Query strings only filter or track; every page is canonical on its bare path.
const canonicalUrl = computed(() => absoluteUrl(route.path))

useHead({
  titleTemplate: title => (title ? `${title} · ${brandName.value}` : brandName.value),
  htmlAttrs: {
    lang: locale
  },
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'description', content: metaDescription }
  ],
  link: computed(() => [
    { rel: 'canonical', href: canonicalUrl.value },
    { rel: 'alternate', hreflang: 'es', href: absoluteUrl(switchLocalePath('es')) },
    { rel: 'alternate', hreflang: 'en', href: absoluteUrl(switchLocalePath('en')) },
    { rel: 'alternate', hreflang: 'x-default', href: absoluteUrl(switchLocalePath('es')) }
  ]),
  style: computed(() => [{
    key: 'tenant-appearance',
    innerHTML: tenantAppearanceCss(branding.context?.appearance ?? null)
  }])
})

useSeoMeta({
  ogSiteName: brandName,
  ogType: 'website',
  ogUrl: canonicalUrl,
  ogLocale: () => (locale.value === 'en' ? 'en_US' : 'es_CO'),
  ogTitle: brandName,
  ogDescription: metaDescription,
  ogImage: absoluteUrl('/pwa/icon-512.png'),
  twitterCard: 'summary'
})
</script>

<template>
  <UApp :locale="uiLocale">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <TqSystemLoadingOverlay />
    <TqToastHost />
    <DataProcessingPolicyModal />
    <ClientOnly><PwaInstallButton /></ClientOnly>
<!-- impeccable-live-start -->
    <ClientOnly v-if="isImpeccableLiveEnabled">
      <component :is="'script'" src="http://localhost:8400/live.js"></component>
    </ClientOnly>
<!-- impeccable-live-end -->
  </UApp>
</template>
